import { useEffect, useRef, VideoHTMLAttributes } from 'react'

import { useLoaderStore } from '@/store/loader'

import { cn } from '@/lib/utils'

type HeroVideoProps = VideoHTMLAttributes<HTMLVideoElement> &
  Partial<{
    video: {
      url: string
    }
    poster: {
      url: string
    }
    canPlay?: boolean
    onCanPlay?: () => void
    onPosterReady?: () => void
  }>

const HeroVideo = ({
  video,
  poster,
  canPlay = false,
  className,
  onCanPlay,
  onPosterReady,
  ...props
}: HeroVideoProps) => {
  const videoRef = useRef<HTMLVideoElement>(null!)
  const heroCanPlay = useLoaderStore((state) => state.heroCanPlay)
  const ready = useRef<boolean>(false)

  useEffect(() => {
    if (!poster?.url) return

    const img = new Image()
    img.src = poster.url

    img.onload = () => onPosterReady?.()

    img.onerror = () => onPosterReady?.()

    return () => {
      img.onload = null
      img.onerror = null
    }
  }, [poster, onPosterReady])

  useEffect(() => {
    if (ready.current) return

    const currentVideo = videoRef.current
    const handleCanPlay = () => {
      ready.current = true
      onCanPlay?.()
    }

    if (canPlay || heroCanPlay) {
      const sources = currentVideo.querySelectorAll('source')
      sources.forEach(
        (source) => (source.src = source.getAttribute('data-src')!)
      )
      currentVideo.addEventListener('canplaythrough', handleCanPlay)
      currentVideo.play()
      currentVideo.load()
    }

    return () => {
      currentVideo.addEventListener('canplaythrough', handleCanPlay)
    }
  }, [canPlay, heroCanPlay, onCanPlay])

  return (
    <>
      <video
        ref={videoRef}
        loop
        muted
        autoPlay
        playsInline
        preload="auto"
        className={cn(className)}
        poster={poster?.url}
        data-lazy-video
        {...props}
      >
        <source data-src={video?.url} type="video/mp4" />
      </video>
    </>
  )
}

export default HeroVideo
